import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { ConnectionVersion, LONG_PLAIN_TEXT_LENGTH } from '../../../../../../constants/common.constants';
import { calcByteCodeSize } from '../../../../../../infrastructure/utils/common.utils';
import { MeetingFormTemplatesVM } from '../../_meeting-form/meeting-form-templates.viewmodel';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import { IMeetingTemplateDTO } from '../../../../../../api/blueprints/meeting.template.types';
import { TObject } from '../../../../../../infrastructure/type-extensions';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { IBlueprintsDAO } from '../../../../../../api/blueprints/blueprints.dao';
import { CO_PILOT_MESSAGE_REQUIRED, CO_PILOT_MESSAGE_TOO_LONG, GENERATION_RESPONSE_TIMEOUT_ERR_MESSAGE, TEXT_AREA_LABEL, } from './meeting-co-pilot.texts';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { IntlShape } from 'react-intl';
import { IAIFeatureDAO, IGeneratedMeeting, IOnMeetingsCreateDataGenerationCompletedResponse } from '../../../../../../api/ai-feature.dao';
import { IAppsyncSubscription, subscribeAppsyncData } from '../../../../../../infrastructure/utils/appsync.utils';
import { onMeetingsCreateDataGenerationCompleted } from '../../../../../../graphql/subscriptions';
import { ERROR_TEXT } from '../../../../../../constants/common-labels.constants';
import { IMeetingSaveDTO, IMeetingsDAO } from '../../../../../../api/meetings.dao';
import { IEventDetailsDTO } from '../../../../../../api/events.dao';
import { IApiDateConverter } from '../../../../../../infrastructure/api-date-converter';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { ACTION_STATUS, AI_FEATURE_TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';


const isBlank = (text: string): boolean => !text || text.trim().length === 0;

class MeetingCoPilotVM {
    public static handleApiException(e: Error, title: string = 'Error'): void {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    }

    private static readonly timeout = 60000; // 60 sec

    @mobx.observable
    public meetingCoPilotText: string;
    @mobx.observable
    public templatesVM: MeetingFormTemplatesVM;
    @mobx.observable
    public generatedMeetings: IMeetingSaveDTO[] = [];
    @mobx.observable
    public isModalOpened: boolean = false;
    @mobx.observable
    public isMeetingsDataGenerated: boolean = false;
    @mobx.observable
    private templates: TObject<IMeetingTemplateDTO>;
    @mobx.observable
    private meetingTemplateId: string | null;
    @mobx.observable
    private isFormChecked: boolean;
    @mobx.observable
    private loading: boolean = false;
    private subscription: IAppsyncSubscription;
    private timeoutId: NodeJS.Timeout | null = null;

    constructor(
        private readonly accountId: number,
        private readonly event: IEventDetailsDTO,
        private readonly onMeetingsCreatedCallback: () => Promise<void>,
        @IoC.Inject private readonly blueprintsDAO: IBlueprintsDAO = IoC.Container.get(IBlueprintsDAO),
        @IoC.Inject private readonly aiFeatureDAO: IAIFeatureDAO = IoC.Container.get(IAIFeatureDAO),
        @IoC.Inject private readonly meetingsDAO: IMeetingsDAO = IoC.Container.get(IMeetingsDAO),
        @IoC.Inject private readonly apiDateConverter: IApiDateConverter = IoC.Container.get(IApiDateConverter),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {
        this.meetingCoPilotText = '';
        this.templates = {};
    }

    public get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public getTemplateList(): IMeetingTemplateDTO[] {
        return Object.values(this.templates);
    }

    public getTemplatesOptions(): { blueprintId: string; name: string }[] {
        const filteredList = this.getTemplateList()
            .filter((template: IMeetingTemplateDTO) => {
                return template.connectionVersion === ConnectionVersion.V2;
            });

        return filteredList.map(({ blueprintId, name }) => ({ blueprintId: blueprintId, name: name }));
    }

    public getMeetingTemplateId(): string | null {
        return this.meetingTemplateId;
    }

    public get isLoading(): boolean {
        return this.loading;
    }

    @mobx.action
    public startLoading(): void {
        this.loading = true;
    }
    @mobx.action
    public stopLoading(): void {
        this.loading = false;
    }

    @mobx.action.bound
    public openModal(): void {
        this.isModalOpened = true;
    }

    @mobx.action.bound
    public closeModal(): void {
        this.isModalOpened = false;
        this.resetState();
    }

    public get eventReqParams(): {accountId: number; eventId: number} {
        return { accountId: this.accountId, eventId: this.event.eventId };
    }

    public get eventTimeZone(): string {
        return this.event.timeZone;
    }

    @mobx.action.bound
    public async startMeetingsGeneration(): Promise<void> {
        try {
            this.startLoading();
            const { identifier } = await this.aiFeatureDAO.getMeetingsGenerationIdentifier(this.eventReqParams);

            await this.subscribe(identifier);
            this.scheduleSubscriptionCancellation();

            await this.aiFeatureDAO.generateMeetingsData({
                identifier: identifier,
                prompt: this.getMeetingCoPilotText(),
                ...this.eventReqParams
            });
        } catch (error) {
            this.subscription?.unsubscribe();
            this.resetSubscriptionCancellation();
            this.stopLoading();
            MeetingCoPilotVM.handleApiException(error);
            this.trackMixPanelEvent(AI_FEATURE_TRACKING_EVENTS.SEND_REQUEST_TO_AI_MEETING_COPILOT, ACTION_STATUS.Failed);
        }
    }

    @mobx.action.bound
    public async saveGeneratedMeetings(): Promise<void> {
        try {
            await this.meetingsDAO.createMeeting(this.generatedMeetings, this.eventReqParams);
            this.trackMixPanelEvent(
                AI_FEATURE_TRACKING_EVENTS.CREATE_MEETING_WITH_AI_MEETING_COPILOT,
                ACTION_STATUS.Success,
                { 'Number of Meetings created': this.generatedMeetings.length }
            );
            this.onMeetingsCreatedCallback();
        } catch (error) {
            this.trackMixPanelEvent(AI_FEATURE_TRACKING_EVENTS.CREATE_MEETING_WITH_AI_MEETING_COPILOT, ACTION_STATUS.Failed);
            MeetingCoPilotVM.handleApiException(error);
        } finally {
            this.closeModal();
        }
    }

    @mobx.action
    public async subscribe(identifier: string): Promise<void> {
        this.subscription = await subscribeAppsyncData<IOnMeetingsCreateDataGenerationCompletedResponse>(
            onMeetingsCreateDataGenerationCompleted,
            { identifier: identifier },
            response => {
                this.resetSubscriptionCancellation();
                this.processSubscriptionResult(response.value.data.onMeetingsCreateDataGenerationCompleted.aiResponse);
            }
        );
    }

    @mobx.action
    public setMeetingTemplateId(id: string): void {
        this.meetingTemplateId = id || null;
    }

    @mobx.action
    public async initializeTemplates(): Promise<void> {
        try {
            const params = { accountId: this.accountId };
            const meetings = await this.blueprintsDAO.getMeetingTemplateList(params);
            this.setMeetingTemplates(meetings);
        } catch (error) {
            MeetingCoPilotVM.handleApiException(error);
        }
    }

    public canApplyTemplateToMeeting(): boolean {
        return (
            hasPermission(Permission.BLUEPRINTS_TAB) &&
            hasPermission(Permission.CREATE_MEETING_FROM_TEMPLATE)
        );
    }

    @mobx.action
    public setMeetingCoPilotText = (value: string): void => {
        this.meetingCoPilotText = value;
    };

    public getMeetingCoPilotText = (): string => {
        return this.meetingCoPilotText;
    };

    public isCoPilotMessageEmpty(): boolean {
        return isBlank(this.meetingCoPilotText);
    }

    public getRequiredCoPilotMessageError(): string | null {
        return this.intl.formatMessage(CO_PILOT_MESSAGE_REQUIRED, {
            field: TEXT_AREA_LABEL.defaultMessage
        });
    }

    public showValidationError(): boolean {
        if (!this.meetingCoPilotText || isBlank(this.meetingCoPilotText)) {
            return this.isFormChecked;
        }
    }

    @mobx.action
    public allowToShowValidationError(): void {
        this.isFormChecked = true;
    }

    public getTooLongCoPilotMessageError = (): string | null => {
        return calcByteCodeSize(this.meetingCoPilotText) > LONG_PLAIN_TEXT_LENGTH
            ? this.intl.formatMessage(CO_PILOT_MESSAGE_TOO_LONG)
            : null;
    };

    @mobx.action.bound
    private processSubscriptionResult(result: IGeneratedMeeting[]): void {
        if (Array.isArray(result)) {
            this.generatedMeetings = result.map(m => this.mapGeneratedMeetingToMeetingDTO(m));
        }
        this.isMeetingsDataGenerated = true;
        this.trackMixPanelEvent(
            AI_FEATURE_TRACKING_EVENTS.SEND_REQUEST_TO_AI_MEETING_COPILOT,
            ACTION_STATUS.Success,
            { 'Number of Meetings returned': result.length }
        );
        this.stopLoading();
    }

    @mobx.action.bound
    private scheduleSubscriptionCancellation(): void {
        this.timeoutId = setTimeout(() => {
            this.subscription?.unsubscribe();
            this.timeoutId = null;
            this.trackMixPanelEvent(AI_FEATURE_TRACKING_EVENTS.SEND_REQUEST_TO_AI_MEETING_COPILOT, ACTION_STATUS.Failed);
            this.stopLoading();
            this.showTimoutError();
        }, MeetingCoPilotVM.timeout);
    }


    @mobx.action.bound
    private resetSubscriptionCancellation(): void {
        clearTimeout(this.timeoutId);
        this.timeoutId = null;
    }

    @mobx.action.bound
    private resetState(): void {
        this.stopLoading();
        this.subscription?.unsubscribe();
        this.resetSubscriptionCancellation();
        this.isMeetingsDataGenerated = false;
        this.meetingCoPilotText = '';
    }

    private showTimoutError(): void {
        alertServerError(
            this.intl.formatMessage(ERROR_TEXT),
            this.intl.formatMessage(GENERATION_RESPONSE_TIMEOUT_ERR_MESSAGE)
        );
    }

    private mapGeneratedMeetingToMeetingDTO(meeting: IGeneratedMeeting): IMeetingSaveDTO {
        // add fields required by API
        return {
            ...meeting,
            startDateTime: this.apiDateConverter.apiStringToDate(meeting.startDateTime),
            endDateTime: this.apiDateConverter.apiStringToDate(meeting.endDateTime),
            blueprintId: null,
            connectionVersion: ConnectionVersion.V2
        } as IMeetingSaveDTO;
    };

    @mobx.action
    private setMeetingTemplates(meetingTemplates: IMeetingTemplateDTO[]): void {
        const templates = {};

        meetingTemplates
            .sort((a, b) => a.name.localeCompare(b.name))
            .forEach(template => {
                templates[template.blueprintId] = template;
            })
        ;

        this.templates = templates;
    }

    private getCommonTrackingProps(): object {
        return {
            'Account ID': this.accountId,
            'Account Name': this.event.accountName,
            'Event ID': this.event.eventId,
            'Event Name': this.event.name
        };
    }

    private async trackMixPanelEvent(eventName: string, status: ACTION_STATUS, props?: object): Promise<void> {
        this.trackingService.get().track(eventName, { Status: status, ...this.getCommonTrackingProps(), ...props });
    }
}

export { MeetingCoPilotVM };
